'use client'

import React, { useState, useCallback, useRef } from 'react'
import {
  ShoppingCart,
  FileText,
  ExternalLink,
  Loader2,
  Check,
} from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useCart } from '@payloadcms/plugin-ecommerce/client/react'
import { toast } from 'sonner'
import { trackAddToCart } from '@/utilities/gtag'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/cn'

interface ChatOrderCTAProps {
  productSlug: string
}

/**
 * Inline "place this order on the website" CTA, shown beneath an
 * assistant message that mentions a product — but ONLY for logged-in
 * customers (guests see the same content in chat, no card). The
 * primary button uses the same `Button` component the product page
 * uses (variant="neon-white"), runs the same `useCart().addItem`
 * flow with `selectedVariantData`, opens the cart drawer, and the
 * customer can continue to /checkout where the existing AGB/terms
 * acceptance lives. Reusing the storefront's button keeps the
 * design language consistent — the chat is a *surface* of the
 * site, not a separate app.
 *
 * Variant products get an inline pill selector. The user picks a
 * size (or whatever) right in the chat, the "In den Warenkorb"
 * button enables, and the selected variant goes through with the
 * addItem call. No redirect to /products/[slug] for variant
 * picking — that was the v1 fallback and it broke the one-click
 * flow.
 *
 * The slug → {id, hasVariants, variants} lookup is a one-shot fetch
 * via /api/products/by-slug (cached 30s server-side). See
 * docs/chatbot-sprite/19.
 */
type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | {
      kind: 'ready'
      id: number | string
      hasVariants: boolean
      title: string
      variants: ChatVariant[] | null
    }
  | { kind: 'notFound' }
  | { kind: 'error' }

interface ChatVariantOption {
  id?: string | number
  value: string
  price: number
  sku?: string
}
interface ChatVariant {
  id?: string | number
  name: string
  values: ChatVariantOption[]
}

export const ChatOrderCTA: React.FC<ChatOrderCTAProps> = ({ productSlug }) => {
  const locale = useLocale() as 'de' | 'en'
  const tProduct = useTranslations('product') // "In den Warenkorb" / "Add to Cart"
  const tCommon = useTranslations('common') // re-use shared copy if available
  const { addItem, isLoading: isCartLoading } = useCart()
  const [state, setState] = useState<LookupState>({ kind: 'idle' })
  const [adding, setAdding] = useState(false)
  const [justAdded, setJustAdded] = useState(false)
  // Map<variantName, selectedValue> — tracks the user's picks. We
  // key by variant name (not id) so it lines up with how
  // AddToCart.tsx / cart's selectedVariantData format the options.
  const [picked, setPicked] = useState<Record<string, string>>({})
  // Re-entrancy guard for the async lookup (in case a user double-
  // clicks before the first fetch resolves).
  const inflightRef = useRef<Promise<LookupState> | null>(null)

  const productHref = `/${locale}/products/${productSlug}`

  // Look up the product on first click rather than on mount, so the
  // CTA costs nothing for customers who never interact with it.
  const ensureLookup = useCallback(async (): Promise<LookupState> => {
    if (state.kind === 'ready') return state
    if (state.kind === 'notFound' || state.kind === 'error') {
      inflightRef.current = null
    }
    if (inflightRef.current) return inflightRef.current
    setState({ kind: 'loading' })
    const p = (async (): Promise<LookupState> => {
      try {
        const res = await fetch(
          `/api/products/by-slug?slug=${encodeURIComponent(productSlug)}`,
          { credentials: 'include' },
        )
        if (res.status === 404) {
          setState({ kind: 'notFound' })
          return { kind: 'notFound' }
        }
        if (!res.ok) {
          setState({ kind: 'error' })
          return { kind: 'error' }
        }
        const data = (await res.json()) as {
          id: number | string
          hasVariants: boolean
          title: string
          variants: ChatVariant[] | null
        }
        const next: LookupState = {
          kind: 'ready',
          id: data.id,
          hasVariants: Boolean(data.hasVariants),
          title: data.title,
          variants: Array.isArray(data.variants) ? data.variants : null,
        }
        setState(next)
        return next
      } catch {
        setState({ kind: 'error' })
        return { kind: 'error' }
      } finally {
        inflightRef.current = null
      }
    })()
    inflightRef.current = p
    return p
  }, [productSlug, state])

  // True when we have a variant product and the user has picked a
  // value for EVERY variant group. Drives the disabled state of the
  // primary button.
  const allVariantsPicked = (() => {
    if (state.kind !== 'ready' || !state.hasVariants || !state.variants) return true
    if (state.variants.length === 0) return true
    return state.variants.every((v) => picked[v.name])
  })()

  const handleClick = useCallback(async () => {
    const lookup = await ensureLookup()

    if (lookup.kind === 'notFound' || lookup.kind === 'error') {
      // Network / slug problem — fall back to the product page so
      // the customer isn't stuck. They can still use the
      // storefront's own AddToCart.
      window.location.assign(productHref)
      return
    }
    if (lookup.kind !== 'ready') return
    if (lookup.hasVariants && !allVariantsPicked) {
      // Defensive: the button is disabled in this state, but if
      // someone reaches the handler (Enter key, scripted click),
      // surface the miss in the toast.
      toast.error(
        locale === 'en'
          ? 'Please select all options first'
          : 'Bitte wähle zuerst alle Optionen',
      )
      return
    }

    // Build the selectedVariantData the cart expects when this is
    // a variant product. Same shape src/components/Cart/AddToCart.tsx
    // and src/collections/Cart/index.ts's customSubtotalHook use.
    let selectedVariantData: any = undefined
    if (lookup.hasVariants && lookup.variants) {
      const options: Array<{ name: string; value: string; price: number; sku?: string }> = []
      let priceTotal = 0
      for (const v of lookup.variants) {
        const pickedValue = picked[v.name]
        const opt = v.values.find((val) => val.value === pickedValue)
        if (!opt) continue
        options.push({
          name: v.name,
          value: opt.value,
          price: opt.price,
          sku: opt.sku,
        })
        priceTotal += opt.price
      }
      if (options.length > 0) {
        selectedVariantData = { options, priceTotal }
      }
    }

    setAdding(true)
    try {
      await addItem(
        { product: lookup.id, selectedVariantData } as any,
        1,
      )
      setJustAdded(true)
      window.dispatchEvent(new CustomEvent('open-cart'))
      trackAddToCart(
        { id: lookup.id, title: lookup.title } as any,
        selectedVariantData?.priceTotal ?? 0,
        1,
        selectedVariantData?.options
          ?.map((o: any) => `${o.name}: ${o.value}`)
          .join(', '),
      )
      toast.success(
        tProduct('addedToCart') ??
          (locale === 'en' ? 'Added to cart' : 'In den Warenkorb gelegt'),
      )
      setTimeout(() => setJustAdded(false), 1800)
    } catch {
      toast.error(
        locale === 'en'
          ? 'Could not add to cart — please try again.'
          : 'Konnte nicht in den Warenkorb gelegt werden — bitte erneut versuchen.',
      )
    } finally {
      setAdding(false)
    }
  }, [addItem, allVariantsPicked, ensureLookup, locale, picked, productHref, tProduct])

  // --- Render -----------------------------------------------------

  // Show the inline variant picker once we know the product has
  // variants. We render it as soon as the lookup has fired (so the
  // user can start picking before clicking the button), or
  // eagerly as soon as we know hasVariants=true. We can show it
  // even on a notFound/error because the picker is independent of
  // the addItem call.
  const variants = state.kind === 'ready' ? state.variants : null
  const showVariantPicker =
    state.kind === 'ready' && state.hasVariants && variants && variants.length > 0

  const isLoading = adding || state.kind === 'loading' || isCartLoading
  const buttonDisabled = isLoading || !allVariantsPicked || justAdded

  return (
    <div
      className="mt-2 max-w-full overflow-hidden rounded-xl border border-[var(--neon-orange)]/35 bg-[var(--neon-orange)]/8 shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.06)] dark:bg-[var(--neon-orange)]/10"
      role="group"
      aria-label={
        locale === 'en'
          ? 'Add this product to your cart'
          : 'Produkt in den Warenkorb legen'
      }
    >
      <div className="flex items-center gap-1.5 px-2.5 pt-2 font-mono text-micro font-semibold uppercase tracking-wider text-neon-orange">
        <ShoppingCart size={11} strokeWidth={2.5} aria-hidden="true" />
        <span>{locale === 'en' ? 'Order online' : 'Online bestellen'}</span>
      </div>

      {/* Variant picker — rendered above the button so the user can
          pick before clicking. Same pill style as the product page's
          VariantSelector.tsx. Only shown when the product actually
          has variants AND the user hasn't selected everything yet. */}
      {showVariantPicker && (
        <div className="flex flex-col gap-2 px-2.5 pt-2">
          {variants!.map((v) => (
            <div key={v.id ?? v.name} className="flex flex-col gap-1">
              <span className="font-mono text-micro font-semibold uppercase tracking-widest text-neutral-600 dark:text-white/70">
                {v.name}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {v.values.map((opt) => {
                  const isActive = picked[v.name] === opt.value
                  return (
                    <button
                      key={opt.id ?? opt.value}
                      type="button"
                      onClick={() =>
                        setPicked((prev) => ({ ...prev, [v.name]: opt.value }))
                      }
                      className={cn(
                        'rounded-md border px-2.5 py-1 text-micro font-mono uppercase tracking-widest transition-all duration-200',
                        isActive
                          ? 'border-[var(--neon-orange)] bg-[var(--neon-orange)] text-black font-bold shadow-[0_0_10px_var(--neon-orange)]'
                          : 'border-black/15 bg-white/60 text-neutral-700 hover:border-[var(--neon-orange)] hover:text-black dark:border-white/15 dark:bg-white/5 dark:text-white/80 dark:hover:text-white',
                      )}
                      aria-pressed={isActive}
                    >
                      {opt.value}
                      {typeof opt.price === 'number' && opt.price > 0 ? (
                        <span className="ml-1 opacity-70">€{opt.price}</span>
                      ) : null}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-2.5 pb-2 pt-2">
        <Button
          type="button"
          onClick={handleClick}
          disabled={buttonDisabled}
          variant="neon-white"
          // The product page's CTA already uses
          // variant="neon-white" with this shape; we match it so
          // the chat CTA feels like a surface of the storefront,
          // not a separate app.
          className="w-full font-mono uppercase tracking-wider"
        >
          {justAdded ? (
            <span className="flex items-center gap-1.5">
              <Check size={14} strokeWidth={2.75} aria-hidden="true" />
              {tProduct('addedToCart') ??
                (locale === 'en' ? 'Added to cart' : 'Im Warenkorb')}
            </span>
          ) : isLoading ? (
            <span className="flex items-center gap-1.5">
              <Loader2 size={14} strokeWidth={2.5} className="animate-spin" aria-hidden="true" />
              {tProduct('addToCart')}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <ShoppingCart size={14} strokeWidth={2.5} aria-hidden="true" />
              {showVariantPicker && !allVariantsPicked
                ? locale === 'en'
                  ? 'Choose option'
                  : 'Option wählen'
                : tProduct('addToCart')}
            </span>
          )}
        </Button>
      </div>
    </div>
  )
}

/**
 * Extract the FIRST product slug referenced in a markdown message.
 * Returns null if the message contains no /products/{slug} link.
 * Used by ChatPanel to decide whether to render a ChatOrderCTA under
 * the message. The bot's product lines always end with a markdown
 * link to the product page, so a single match per message is enough.
 */
export function extractFirstProductSlug(markdown: string): string | null {
  if (typeof markdown !== 'string' || markdown.length === 0) return null
  const re = /\/(?:en\/)?products\/([a-z0-9][a-z0-9-]{0,120})/i
  const m = re.exec(markdown)
  return m ? m[1] : null
}

/**
 * Find the most recent product slug in a conversation. The primary
 * source is the assistant's CURRENT message. If it has no product
 * link (e.g. a clarification reply — "which variant would you
 * like?"), FALL BACK to the most recent prior assistant message
 * that DID have a link. This keeps the "Add to cart" card alive in
 * the "I want 3x X → bot asks 'which variant?'" flow (bug
 * 2026-07-26 with Borneo Kratom — bot's clarification had no link,
 * CTA couldn't render, user couldn't add to cart). Returns null if
 * no product link is anywhere in the conversation.
 *
 * The function is pure + exported so it can be unit-tested without
 * mounting React.
 */
export function findProductSlugWithFallback<
  T extends { role: 'user' | 'assistant'; content: string },
>(messages: readonly T[], currentIndex: number): string | null {
  if (currentIndex < 0 || currentIndex >= messages.length) return null
  const current = messages[currentIndex]
  if (current?.role === 'assistant') {
    const slug = extractFirstProductSlug(current.content)
    if (slug) return slug
  }
  // Walk backwards through prior assistant messages looking for the
  // most recent one with a product link.
  for (let j = currentIndex - 1; j >= 0; j--) {
    const m = messages[j]
    if (m?.role === 'assistant') {
      const slug = extractFirstProductSlug(m.content)
      if (slug) return slug
    }
  }
  return null
}

export default ChatOrderCTA
